import type { AnalyticsBreakdownItem } from '@shared/api-client';
import * as React from 'react';
import { Card } from '@/components/ui/card';
import { cn, formatCompactNumber } from '@/lib/utils';

export type BreakdownTab = {
  id: string;
  label: string;
  itemLabel?: string;
  items: AnalyticsBreakdownItem[];
  value: 'visitors' | 'events';
};

export const BreakdownPanel = ({ tabs }: { tabs: BreakdownTab[] }) => {
  const [activeId, setActiveId] = React.useState(tabs[0]?.id);
  const active = tabs.find((tab) => tab.id === activeId) ?? tabs[0];
  const maximum = Math.max(
    ...(active?.items.map((item) => item[active.value]) ?? []),
    1,
  );

  return (
    <Card className="h-full overflow-hidden shadow-none">
      <div className="flex gap-1 overflow-x-auto overflow-y-hidden border-b border-border px-4 pt-3">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveId(tab.id)}
            className={cn(
              '-mb-px border-b-2 px-2 py-2 text-xs font-medium whitespace-nowrap transition-colors',
              active?.id === tab.id
                ? 'border-accent text-ink'
                : 'border-transparent text-ink-subtle hover:text-ink',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {!active || active.items.length === 0 ? (
        <div className="flex min-h-44 items-center justify-center px-6 text-center text-sm text-ink-muted">
          No data in this period.
        </div>
      ) : (
        <div className="p-4">
          <div className="mb-2 flex items-center justify-between gap-3 text-xs text-ink-subtle">
            <span>{active.itemLabel ?? active.label.replace(/s$/, '')}</span>
            <span>{active.value === 'events' ? 'Events' : 'Visitors'}</span>
          </div>
          <ol className="space-y-0.5">
            {active.items.map((item) => {
              const value = item[active.value];
              return (
                <li
                  key={item.name}
                  className="relative flex min-h-8 items-center gap-3 px-2"
                >
                  <span
                    className="absolute inset-y-1 left-0 rounded bg-surface-muted"
                    style={{
                      width: `${Math.max((value / maximum) * 100, 2)}%`,
                    }}
                    aria-hidden
                  />
                  <span
                    className="relative min-w-0 flex-1 truncate text-sm text-ink"
                    title={item.name}
                  >
                    {item.name}
                  </span>
                  <span className="relative shrink-0 text-sm tabular-nums text-ink-muted">
                    {formatCompactNumber(value)}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </Card>
  );
};
